import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import { InvoiceMissionSendingSearchfilter } from 'src/shared/models/invoiceMission/iInvoiceMissionSendingSearchfilter';
import { getAllInvoiceMissionSending } from 'src/shared/services/invoiceMissionSendingService';

type InvoiceMissionInvoicesGeneratingIndicatorProps = {
  invoiceMissionId: number;
  sendingType: number;
  setIsGeneratingInvoices: (val: boolean) => void;
  refetchMission: (refetch: boolean) => void;
};

export const InvoiceMissionInvoicesGeneratingIndicator = (props: InvoiceMissionInvoicesGeneratingIndicatorProps) => {
  const { invoiceMissionId, sendingType, setIsGeneratingInvoices, refetchMission } = props;
  const { t } = useTranslation();

  const pullDataIntervalInMS = 5000;
  const stopPollingRef = useRef(false);
  const didRefetchWhenDoneRef = useRef(false);
  const intervalIdRef = useRef<number | null>(null);

  const [firstFetchIsDone, setFirstFetchIsDone] = useState(false);

  useEffect(() => {
    stopPollingRef.current = false;
    didRefetchWhenDoneRef.current = false;
    setIsGeneratingInvoices(true);

    const fetchOnce = async () => {
      try {
        const filter: InvoiceMissionSendingSearchfilter = {
          pageIndex: DEFAULT_PAGE_INDEX,
          pageSize: DEFAULT_PAGE_SIZE,
          sortCol: 'invoiceMission_id',
          sortDir: 'desc' as const,
          sendingType,
          invoiceMissionId,
        };

        const result = await getAllInvoiceMissionSending(filter);
        if (!firstFetchIsDone) setFirstFetchIsDone(true);

        const first = result?.data?.results?.[0];
        const isDone = !!first?.createStartedAt && !!first?.createEndedAt;

        if (isDone) {
          setIsGeneratingInvoices(false);

          if (!didRefetchWhenDoneRef.current) {
            didRefetchWhenDoneRef.current = true;
            refetchMission(true);
          }

          stopPollingRef.current = true;
          if (intervalIdRef.current !== null) {
            window.clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }
        } else {
          setIsGeneratingInvoices(true);
        }
      } catch {
        setIsGeneratingInvoices(false);
      }
    };

    fetchOnce();

    intervalIdRef.current = window.setInterval(() => {
      if (stopPollingRef.current) {
        if (intervalIdRef.current !== null) {
          window.clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
        }
        return;
      }
      fetchOnce();
    }, pullDataIntervalInMS);

    return () => {
      if (intervalIdRef.current !== null) {
        window.clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, [invoiceMissionId, sendingType, setIsGeneratingInvoices, refetchMission, firstFetchIsDone]);

  return (
    <Box role='status' aria-live='polite'>
      <Stack direction='row' spacing={1.5} alignItems='center'>
        {firstFetchIsDone && <Typography fontWeight={500}>{t('invoice:invoiceMissionPage.generatingInvoices')}</Typography>}
        <CircularProgress size={15} />
      </Stack>
      {firstFetchIsDone && <Typography variant='body2'>{t('invoice:invoiceMissionPage.generatingInvoicesInfo')}</Typography>}
    </Box>
  );
};
